import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

const TOPIC_OPTIONS = [
  "Services for myself or a loved one",
  "Making a referral",
  "Support Coordinator inquiry",
  "Employment",
  "General question",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", topic: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "mt-2 w-full rounded-lg bg-white border border-brand-stone/70 px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy/20";
  const labelCls = "text-[11px] font-bold uppercase tracking-[0.22em] text-brand-navy";

  return (
    <div data-testid="contact-page" className="bg-brand-cream">
      <Toaster richColors position="top-right" />

      {/* Hero — warm stone tone to separate from the cream header */}
      <PageHeader
        eyebrow="Contact"
        title="Let's talk about how we can"
        accent="support you"
        kicker="."
        subtitle="Whether you're looking for services, making a referral, or simply have questions, our team is here to help. Connect with WeCare II and we'll help you determine the next step."
        tone="stone"
        data-testid="contact-header"
      />

      {/* Body — soft white section on the cream page */}
      <section className="bg-brand-bone" data-testid="contact-body">
        <div className="wc-container py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-start">

            {/* LEFT — Form inside a subtle warm-stone panel */}
            <div className="bg-brand-sand/60 rounded-[1.75rem] p-6 md:p-10 border border-brand-stone/50" data-testid="contact-form-panel">
              <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-brand-navy leading-snug">
                How can we help?
              </h2>
              <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">
                Tell us a little about what you&apos;re looking for and our team will be in touch.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <label className={labelCls}>Your name</label>
                  <input
                    required
                    data-testid="contact-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                    placeholder="Full name"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Email</label>
                    <input
                      required
                      type="email"
                      data-testid="contact-email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={inputCls}
                      placeholder="you@email.com"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input
                      data-testid="contact-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className={inputCls}
                      placeholder="(optional)"
                    />
                  </div>
                </div>

                <div>
                  <label className={labelCls}>I&apos;m contacting WeCare II about</label>
                  <select
                    required
                    data-testid="contact-topic"
                    value={form.topic}
                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    className={`${inputCls} appearance-none pr-10 bg-white`}
                    style={{
                      backgroundImage:
                        "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%231e2a78' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e\")",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 1rem center",
                    }}
                  >
                    <option value="" disabled>Select a topic…</option>
                    {TOPIC_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelCls}>Message</label>
                  <textarea
                    required
                    rows={5}
                    data-testid="contact-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className={inputCls}
                    placeholder="Share a few details so we can point you in the right direction."
                  />
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange text-white hover:brightness-95 disabled:opacity-60 px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] shadow-sm"
                  style={{ transition: "filter 200ms ease, transform 200ms ease" }}
                >
                  {sending ? "Sending…" : (<>Send Message <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </form>
            </div>

            {/* RIGHT — single cohesive navy contact panel */}
            <div className="space-y-6">
              <div className="relative bg-brand-navy text-white rounded-[1.75rem] p-8 md:p-10 shadow-[0_18px_40px_rgba(15,26,74,0.18)] overflow-hidden" data-testid="contact-panel">
                <div className="inline-flex items-center gap-2.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.28em] text-brand-orange">
                  <span className="h-px w-8 bg-brand-orange" />
                  Connect With WeCare II
                </div>
                <h3 className="mt-4 font-outfit text-2xl md:text-[28px] font-medium tracking-tight leading-snug text-white">
                  We&apos;re here to help.
                </h3>

                <div className="mt-8 space-y-5">
                  <a href="tel:7579519555" className="flex items-start gap-4 group" data-testid="contact-panel-call">
                    <Phone className="w-4 h-4 text-brand-orange mt-1 shrink-0" strokeWidth={1.6} />
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.24em] text-white/60 font-bold">Call us</div>
                      <div className="mt-1 text-[17px] font-outfit font-medium text-white group-hover:text-brand-orange" style={{ transition: "color 200ms ease" }}>757-951-9555</div>
                    </div>
                  </a>
                  <a href="mailto:info@wecareii.com" className="flex items-start gap-4 group" data-testid="contact-panel-email">
                    <Mail className="w-4 h-4 text-brand-orange mt-1 shrink-0" strokeWidth={1.6} />
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.24em] text-white/60 font-bold">Email us</div>
                      <div className="mt-1 text-[17px] font-outfit font-medium text-white group-hover:text-brand-orange" style={{ transition: "color 200ms ease" }}>info@wecareii.com</div>
                    </div>
                  </a>
                </div>

                <div className="my-7 h-px bg-white/10" />

                <div className="text-[10px] uppercase tracking-[0.24em] text-white/60 font-bold">Our Locations</div>
                <div className="mt-5 space-y-5" data-testid="contact-panel-locations">
                  {[
                    {
                      name: "Virginia Beach",
                      line1: "3877 Holland Road, #508-B",
                      line2: "Virginia Beach, VA 23452",
                      dirQ: "3877+Holland+Road+%23508-B,+Virginia+Beach,+VA+23452",
                    },
                    {
                      name: "Eastern Shore",
                      line1: "36304 Lankford Hwy",
                      line2: "Belle Haven, VA 23306",
                      dirQ: "36304+Lankford+Hwy,+Belle+Haven,+VA+23306",
                    },
                  ].map((loc, i) => (
                    <React.Fragment key={loc.name}>
                      {i > 0 && <div className="h-px bg-white/10" aria-hidden="true" />}
                      <div className="flex items-start gap-4">
                        <MapPin className="w-4 h-4 text-brand-orange mt-1 shrink-0" strokeWidth={1.6} />
                        <div className="flex-1">
                          <div className="font-outfit font-medium text-white text-[15px] tracking-tight">{loc.name}</div>
                          <div className="mt-0.5 text-sm font-normal text-white/70 leading-relaxed">
                            {loc.line1}<br />{loc.line2}
                          </div>
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${loc.dirQ}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-brand-orange hover:brightness-110"
                            data-testid={`contact-directions-${loc.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            Get Directions <ArrowRight className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Support Coordinator callout — compact, warm cream + thin orange accent line */}
              <div className="relative bg-brand-cream rounded-2xl border border-brand-stone/60 p-6 md:p-7 overflow-hidden" data-testid="contact-sc-callout">
                <span className="absolute top-0 left-8 right-8 h-[2px] bg-brand-orange" aria-hidden="true" />
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                  <div className="max-w-md">
                    <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-brand-orange">For Support Coordinators</div>
                    <div className="mt-2 font-outfit text-[19px] font-semibold text-brand-navy tracking-tight leading-snug">Making a referral?</div>
                    <p className="mt-1.5 text-[14px] text-slate-600 leading-relaxed">
                      We work directly with Support Coordinators and care teams to help make the referral process clear and responsive.
                    </p>
                  </div>
                  <Link
                    to="/refer"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white hover:bg-brand-navydark px-6 py-3 text-[11px] font-bold uppercase tracking-[0.22em] shrink-0"
                    style={{ transition: "background-color 200ms ease" }}
                    data-testid="contact-refer-btn"
                  >
                    Make a Referral <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
